import React from 'react';
import { Document, Page, View, StyleSheet, Text, Image, Svg, Line, Font } from '@react-pdf/renderer';
import PdfTable from './PdfTable';
import MainLogo from '../assets/main-logo.png';
import Signature from '../assets/Unterschrift_Okan.png';
import dayjs from 'dayjs';
import { IOffer } from '../interface/IOffer';
import UVC from '../assets/webfonts/UVC_____.TTF';
import UVCL from '../assets/webfonts/UVCL____.TTF';
import { IClient } from '../interface/IClient';
import { IInvoice } from '../interface/IInvoice';
import { calculateOfferPostionsTotalProjectPrice, deutschNumberFormatter } from '../helper/utils';
import InvoicePdfTable from './InvoicePdfTable';

// import UVCLBold from '../assets/webfonts/UVCB____.TTF';

Font.register({
  family: 'uvcl',
  fonts: [{ src: UVCL }, { src: UVC, fontWeight: 'bold' }],
});

const styles = StyleSheet.create({
  page: {
    paddingTop: 35,
    paddingBottom: 80,
    paddingHorizontal: 48,
    boxSizing: 'border-box',
    fontFamily: 'uvcl',
    fontSize: 10,
  },

  logo: {
    width: 145,
    objectFit: 'contain',
    alignSelf: 'flex-end',
    marginBottom: 10,
  },
  view: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  greyView: {
    padding: '5px 5px 5px 15px',
    backgroundColor: '#bfbfbf',
    // textAlign: 'right',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    alignSelf: 'flex-start',
  },
  greyViews: {
    padding: '5px 15px 5px 5px',
    backgroundColor: '#d9d9d9',
    flexDirection: 'column',
    alignSelf: 'flex-start',
  },
  signature: {
    width: 100,
    marginVertical: 5,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 48,
    right: 48,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
  },
  priceBoxContainer: {
    padding: 2,
    width: '40%',
    alignSelf: 'flex-end',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  priceBoxTotalContainer: {
    fontWeight: 'bold',
    backgroundColor: '#f0f0f0',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderBottomColor: '#747474',
    borderTopColor: '#b6b6b6',
  },
  priceBox: {
    width: '15%',
    padding: 2,
    borderStyle: 'solid',
    borderWidth: 1,
    borderLeftWidth: 1,
    borderTopWidth: 1,
  },
  linebreak: {
    marginBottom: 10,
  },
});

type Props = {
  invoice: IInvoice;
  offer: Partial<IOffer>;
  client?: IClient | null;
  invoicePositions: any;
};

const InvoicePdf = ({ invoice, offer, invoicePositions, client }: Props) => {
  return (
    <Document>
      <Page size='A4' style={styles.page}>
        <Image style={styles.logo} src={MainLogo} fixed />
        <View>
          <View style={styles.view}>
            <View style={{ marginTop: 5 }}>
              <Text style={{ marginLeft: 3, marginBottom: 3, fontSize: 8 }}>
                public<Text style={{ color: '#8c2528', fontWeight: 'bold' }}>seven</Text> GmbH | Hofaeckerallee 19 | D-85774 Unterfoehring
              </Text>
              <Svg height={5} width={300}>
                <Line x1='0' y1='0' x2='200' y2='0' strokeWidth={2} stroke='#8c2528' />
              </Svg>
              <Text>{offer.invoiceToName}</Text>
              <Text>{offer.invoiceToStreet}</Text>
              <Text>
                {offer.invoiceToPostalCode} {offer.invoiceToCity} {offer.invoiceToCountryCode}
              </Text>
            </View>
            <View style={styles.view}>
              <View style={styles.greyView}>
                <Text>Name</Text>
                <Text>Phone</Text>
                <Text>Mobile</Text>
                <Text>Email</Text>
                <Text style={styles.linebreak}>Web</Text>
                <Text>Date</Text>
              </View>
              <View style={styles.greyViews}>
                <Text>Okan Reis</Text>
                <Text>+49 (0) 89 3181 3922</Text>
                <Text>+49 (0) 151 167 69 162</Text>
                <Text>okan.reis@publicseven.com</Text>
                <Text style={styles.linebreak}>www.publicseven.com</Text>
                <Text>{dayjs(invoice.invoiceDate).format('DD.MM.YYYY')}</Text>
              </View>
            </View>
          </View>
          <View style={{ paddingTop: 30, paddingBottom: 30 }}>
            <View style={styles.view}>
              <Text>
                {offer.title} | Offer Id: {offer.offerId || 'N/a'}
              </Text>
              <Text style={{ fontWeight: 'bold' }}>Invoice Id: {invoice.invoiceId}</Text>
            </View>
            <Text>
              Contact Person: {client?.firstname} {client?.lastname}, {client?.department}
            </Text>
            <Text>Contract number: {offer.contractNumber}</Text>
          </View>
          <View>
            <Text>We hereby ask you to settle the invoice amount.</Text>
            <Text>
              Period of service provision: {dayjs(invoice.invoiceFrom).format('DD.MM.YYYY')} - {dayjs(invoice.invoiceTo).format('DD.MM.YYYY')}
            </Text>
          </View>
          <View style={{ marginTop: 5 }}>
            {invoicePositions && <InvoicePdfTable data={invoicePositions} discount={`${offer.discount} %`} />}
            <View style={[styles.priceBoxContainer, { fontWeight: 'bold', marginTop: 10 }]}>
              <Text>Total Amount (Taxable):</Text>
              <View>
                <Text style={{ textAlign: 'right' }}>{deutschNumberFormatter(calculateOfferPostionsTotalProjectPrice(invoicePositions))} €</Text>
              </View>
            </View>
            <View style={styles.priceBoxContainer}>
              <Text>Plus 19% VAT:</Text>
              <View>
                <Text style={{ textAlign: 'right' }}>
                  {deutschNumberFormatter(calculateOfferPostionsTotalProjectPrice(invoicePositions) * 0.19)} €
                </Text>
              </View>
            </View>
            <View style={[styles.priceBoxContainer, styles.priceBoxTotalContainer]}>
              <Text>Total (gross):</Text>
              <View>
                <Text style={{ textAlign: 'right' }}>
                  {deutschNumberFormatter(calculateOfferPostionsTotalProjectPrice(invoicePositions) * 1.19)} €
                </Text>
              </View>
            </View>
          </View>
          <View style={{ marginTop: 15 }}>
            <Text style={styles.linebreak}>
              We kindly ask you to transfer the invoice amount of{' '}
              {deutschNumberFormatter(calculateOfferPostionsTotalProjectPrice(invoicePositions) * 1.19)} EUR within 30 days without deduction to our
              named account.
            </Text>
            <Text>Best Regards</Text>
            <Image style={styles.signature} src={Signature} />
            <Text>Okan Reis</Text>
          </View>
        </View>
        <View style={styles.footer} fixed>
          <View>
            <Text>
              public<Text style={{ color: '#8c2528', fontWeight: 'bold' }}>seven</Text> GmbH
            </Text>
            <Text>Hofaeckerallee 19</Text>
            <Text>D 85774 Unterfoehring</Text>
            <Text>Tax ID: DE220815698</Text>
          </View>
          <View>
            <Text>Branch Office:</Text>
            <Text>Technologiepark 9</Text>
            <Text>D 33100 Paderborn</Text>
          </View>
          <View>
            <Text>Managing director: Okan Reis</Text>
            <Text>Commercial Register ID: HRB 272686</Text>
            <Text>Commercial Register: Amtsgericht München</Text>
          </View>
          <View>
            <Text>HypoVereinsbank München</Text>
            <Text>IBAN DE46 7212 0078 0034 5426 86</Text>
            <Text>BIC HYVEDEMM426</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePdf;
